import { Div, Button, router, state, IRouteEvent } from '@codesuma/baseline'
import { Slider, ISliderFill } from '../../components/slider'
import { BackIcon } from '../../components/back-icon'
import { DaySlide, IDayAnchor } from './day-slide'
import { TabBar, DayTab } from './tab-bar'
import calendar, { IDayCell } from '../../services/calendar'
import settings from '../../services/settings'
import { paneled } from '../../helpers/panes'
import style from './index.module.css'

// The day view (route /day?d=gy-gm-gd): a day slider anchored on the tapped
// date, with a tab bar under it choosing which of the day's three tabs the
// slides show.
//
// On a phone it is a sheet that comes up from the bottom. Opened from the
// calendar, it comes up over it: the calendar stays, rises a little and rings
// the day, and the sheet stops just under the day's row (see lift in
// pages/home/month-slide). Pulled down, by the bar along its top edge or from
// anywhere with the list at its top, it goes back down. On a wide screen it is
// the panel beside the calendar instead, and the back chevron closes it.
//
// Not baseline's Page(): a Page moves as one piece, after a pause, and here
// the sheet has to move at once, in step with the calendar under it. The
// page's three states are classes on base — see open(), leave() and close().
export const DayPage = () => {
    const base = Div()
    base.addClass(style.base)
    base.attr('aria-hidden', 'true')

    let anchor: IDayAnchor = calendar.today()
    let hasBack = false // false on deep links, where history has no entry to go back to
    // Which tab the slides show. Kept across visits, the way a tab bar's choice
    // is: the page is cached, and someone reading notes wants the next day's
    // notes too.
    let tab: DayTab = 'agenda'
    // Set when the notes tab has just been picked, and cleared by the first
    // slide filled after it. Only that switch opens the keyboard: doing it on
    // every fill would reopen it on every swipe and on every redraw.
    let focusNotes = false
    // Whether the sheet stands over the calendar: opened from it, on a phone.
    // Opened from anywhere else — a search result, a link — there is no
    // calendar under it to rise, and the sheet takes the whole height.
    let over = false

    const back = () => hasBack ? router.back() : router.goto('/')

    const sheet = Div()
    sheet.addClass(style.sheet)
    base.append(sheet)

    const slider = Slider()
    slider.on('fill', ({ slide, index }: ISliderFill) => {
        slide.empty()
        const day = DaySlide(anchor, index, tab)
        // The slide passes its own day, not the anchor — the user may have
        // swiped several days along before reaching for the button.
        day.on('add-task', (c: IDayCell) => router.goto(`/task?d=${c.gy}-${c.gm}-${c.gd}`))
        // Redraw the window, not just this slide: a task put off to tomorrow
        // belongs to the slide already sitting beside this one, and that one
        // was filled before the move. Ticking one off does not come through
        // here — the row marks itself and the list stays under the finger.
        day.on('tasks-changed', () => slider.fill())
        // The one editor, with the day already named so the wizard's first step
        // is what kind rather than which day — the month's own list opens the
        // same page without one.
        day.on('add-happening', (c: IDayCell) => router.goto(`/event?d=${c.gy}-${c.gm}-${c.gd}`))
        // A note is written in the tab itself; only changing one leaves for
        // the note page.
        day.on('open-note', (id: string) => router.goto(`/note?id=${id}`))
        day.on('open-event', (route: string) => router.goto(route))
        slide.append(day)
        // fill() renders the visible slide first and its neighbours on the
        // next quiet moment, so this is the one the reader is looking at.
        if (focusNotes && slide.getPos() === 1) {
            focusNotes = false
            day.focusNotes()
        }
    })
    sheet.append(slider)

    // The calendar under the sheet follows it from day to day: this tells it
    // which day the sheet is on, and it rings that day, rises to that month
    // and says where the sheet now stops. The day comes off the slider, not
    // off the 'change' event: that is a DOM event's name as well, and the notes
    // field's own change bubbles up through the slider to here.
    const publish = () => {
        if (over) state.set('day-view', calendar.dayAt(anchor.gy, anchor.gm, anchor.gd, slider.getCursor()))
    }
    slider.on('change', publish)

    // And the other way: a day tapped on the calendar above the sheet. The
    // sheet goes to it, by a swipe when it is the day after or the day before,
    // the way a finger would take it there.
    const pick = (c: IDayCell) => {
        const index = Math.round((Date.UTC(c.gy, c.gm - 1, c.gd) - Date.UTC(anchor.gy, anchor.gm - 1, anchor.gd)) / 864e5)
        const cursor = slider.getCursor()
        if (index === cursor + 1) slider.next()
        else if (index === cursor - 1) slider.prev()
        else slider.goto(index)
    }
    state.on('day-pick', pick)

    // Where the sheet stops over the calendar, which the calendar measures (see
    // placeSheet in pages/home). Written on this page, not on the document: a
    // custom property changed on the root restyles every element in the app,
    // and only the sheet reads this one.
    const stop = (top: number) => base.el.style.setProperty('--day-sheet-top', `${top}px`)
    state.on('day-sheet-top', stop)

    // Switching tabs redraws the three slides on the new list. A refill rather
    // than showing and hiding inside each slide: it is what every other change
    // to the page already does, and it starts the new list at its top.
    const tabs = TabBar(tab)
    tabs.on('pick', (picked: DayTab) => {
        if (picked === tab) return
        tab = picked
        tabs.select(tab)
        // Picking notes means wanting to write one, so the field is ready.
        focusNotes = tab === 'notes'
        slider.fill()
    })
    sheet.append(tabs)

    // Gregorian mode reads LTR, so the slider advances that way too — and the
    // page turns with it, so the dismiss in its leading corner stays on the
    // side each slide's title starts from.
    const syncDirection = () => {
        const ltr = settings.get().system === 'gregorian'
        slider.direction(ltr ? 'ltr' : 'rtl')
        base.attr('dir', ltr ? 'ltr' : 'rtl')
    }

    // The way out on a wide screen, where there is no sheet to pull down.
    const dismiss = Button()
    dismiss.addClass(style.dismiss)
    dismiss.attr('aria-label', 'برگشت')
    dismiss.attr('data-ripple', '')
    dismiss.append(BackIcon())
    dismiss.on('click', back)
    sheet.append(dismiss)

    // The bar along the sheet's top edge is the way back on a phone: tap it, or
    // pull the sheet down by it. Let go far enough down, or with a flick, and
    // the sheet goes the rest of the way; anything less and it springs back.
    const grab = Button()
    grab.addClass(style.grab)
    grab.attr('aria-label', 'برگشت')
    sheet.append(grab)

    const follow = (dy: number) => sheet.style({ transition: 'none', transform: `translateY(${dy}px)` })
    const springBack = () => sheet.style({ transition: '', transform: '' })
    const letGo = (dy: number, ms: number) => {
        const flick = dy > 20 && dy / ms > 0.5 // px per ms
        // Going: on down from where the finger let go. close() sets nothing
        // this has not already set, and the next open() clears it.
        if (dy > 100 || flick) {
            sheet.style({ transition: '', transform: 'translateY(100%)' })
            back()
        } else {
            springBack()
        }
    }

    let pull: { id: number, y: number, t: number, dy: number } | null = null
    // A pull ends in a click as well, and that click must not count as a tap.
    let pulled = false
    grab.el.addEventListener('pointerdown', (e: PointerEvent) => {
        pull = { id: e.pointerId, y: e.clientY, t: e.timeStamp, dy: 0 }
        pulled = false
        grab.el.setPointerCapture(e.pointerId)
    })
    grab.el.addEventListener('pointermove', (e: PointerEvent) => {
        if (!pull || e.pointerId !== pull.id) return
        pull.dy = Math.max(0, e.clientY - pull.y)
        if (pull.dy > 4) pulled = true
        if (pulled) follow(pull.dy)
    })
    grab.el.addEventListener('pointerup', (e: PointerEvent) => {
        if (!pull || e.pointerId !== pull.id) return
        const { dy, t } = pull
        pull = null
        if (!pulled) return // a tap: the click has it
        letGo(dy, e.timeStamp - t)
    })
    grab.el.addEventListener('pointercancel', (e: PointerEvent) => {
        if (!pull || e.pointerId !== pull.id) return
        pull = null
        springBack()
    })

    // The rest of the sheet pulls down too, the way a phone's own sheets do:
    // a drag down from a list that is already at its top. Anywhere else the
    // drag is the list's own scroll. Touch events, not pointer events, because
    // only a touchmove can keep the browser from taking the drag as a scroll,
    // and it has to say so on the first moves, before the browser commits.
    // Until the finger has gone 8px either way nothing is decided: the
    // slider's own figure, so a swipe to the next day and a pull never both
    // start. A dialog on a slide keeps its touches.
    let drag: { x: number, y: number, t: number, dy: number, on: boolean } | null = null
    const atTop = (el: Element | null) => {
        for (; el && el !== sheet.el; el = el.parentElement) if (el.scrollTop > 0) return false
        return true
    }
    sheet.el.addEventListener('touchstart', (e: TouchEvent) => {
        drag = null
        const target = e.target as Element
        if (e.touches.length !== 1 || paneled() || grab.el.contains(target)) return
        if (target.closest('[role="dialog"]') || !atTop(target)) return
        drag = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: e.timeStamp, dy: 0, on: false }
    }, { passive: true })
    sheet.el.addEventListener('touchmove', (e: TouchEvent) => {
        if (!drag) return
        const dx = e.touches[0].clientX - drag.x
        const dy = e.touches[0].clientY - drag.y
        if (!drag.on) {
            if (Math.abs(dx) <= 8 && Math.abs(dy) <= 8) {
                // Down from the top has nothing to scroll, so holding the
                // browser off costs nothing while this is decided.
                if (dy > 0) e.preventDefault()
                return
            }
            if (dy <= Math.abs(dx)) {
                drag = null
                return
            }
            drag.on = true
        }
        e.preventDefault()
        drag.dy = Math.max(0, dy)
        follow(drag.dy)
    }, { passive: false })
    sheet.el.addEventListener('touchend', (e: TouchEvent) => {
        if (drag?.on) letGo(drag.dy, e.timeStamp - drag.t)
        drag = null
    })
    sheet.el.addEventListener('touchcancel', () => {
        if (drag?.on) springBack()
        drag = null
    })
    grab.el.addEventListener('click', () => {
        if (pulled) pulled = false
        else back()
    })

    // Up, from wherever it was: closed, or under a page it opened. The closed
    // position has to have been drawn before the open one is set, or there is
    // nothing for the sheet to travel from — the first time, base went into the
    // document a moment ago. Whatever a pull left on the sheet goes first.
    const open = () => {
        // Back from under a page it opened: the calendar comes back down with
        // it (see leave).
        if (over && base.el.classList.contains(style.behind)) state.set('day-view-away', false)
        sheet.style({ transition: '', transform: '' })
        void base.el.offsetHeight
        base.removeClass(style.behind)
        base.addClass(style.open)
        base.attr('aria-hidden', 'false')
    }

    // A page this one opened is coming up over it: a task, a note, the menu.
    // This page makes way the way every page does — up and out of sight, see
    // .behind — and so does the calendar under the sheet, which the day view
    // has made part of it (see pages/home): taking the sheet alone away would
    // show the month's lists through it. The sheet stays open, and open()
    // brings both back down when that page goes.
    const leave = () => {
        base.addClass(style.behind)
        base.attr('aria-hidden', 'true')
        if (over) state.set('day-view-away', true)
    }

    // Down, and the calendar under it settles back.
    const close = () => {
        base.removeClass(style.open, style.behind)
        base.attr('aria-hidden', 'true')
        if (over) state.set('day-view', null)
    }

    // Drawn once, out of sight, before anyone opens it (app.ts asks, soon after
    // launch). The first time the sheet is drawn, the GPU has to work out how
    // to draw its shadow, its rounded corners and the tab bar's blur, and
    // shows nothing for half a second meanwhile. This gets that done early. It
    // opens the way a link to today does, at an opacity no one can see (see
    // .warming), and closes again. A tap on a day meanwhile puts it away
    // first, so the page opens from closed as it always does.
    let warming = 0
    const cool = () => {
        clearTimeout(warming)
        warming = 0
        close()
        // Hidden while still out of sight, so it does not show on the way.
        void base.el.offsetHeight
        base.removeClass(style.warming)
    }
    const warm = () => {
        base.addClass(style.warming)
        base.emit('enter', { params: {}, query: {}, from: '' })
        base.attr('aria-hidden', 'true')
        warming = window.setTimeout(cool, 500)
    }

    let anchored = '' // the query this page is currently showing

    base.on('enter', ({ query, from }: IRouteEvent) => {
        if (warming) cool()
        // Decided as the sheet comes up, and kept while pages it opens come and
        // go over it. First, because rewinding the slider below already tells
        // the calendar about the day — or must not, when it is not under it.
        if (!base.el.classList.contains(style.open)) over = from === '/' && !paneled()
        base.toggleClass(style.over, over)
        // Anchor on the requested day; fall back to today (bad or missing query).
        const d = String((query as Record<string, string>)?.d || '')
        const parts = d.split('-').map(Number)
        if (parts.length === 3 && parts.every(n => Number.isFinite(n) && n > 0)) {
            anchor = { gy: parts[0], gm: parts[1], gd: parts[2] }
        } else {
            const t = calendar.today()
            anchor = { gy: t.gy, gm: t.gm, gd: t.gd }
        }
        // A search result names the list it was found in, so the page opens
        // on that tab rather than on whichever was last read.
        const t = String((query as Record<string, string>)?.t || '')
        if ((t === 'agenda' || t === 'notes') && t !== tab) {
            tab = t
            tabs.select(tab)
            focusNotes = false
        }
        syncDirection()
        // Coming back from the occasion page re-enters on the same day, so the
        // slider stays where the user swiped it to; a different day rewinds it.
        if (d !== anchored) {
            anchored = d
            if (slider.getCursor() !== 0) slider.reset()
            else slider.fill()
        } else {
            slider.fill()
        }
        // Before open(), so the calendar has said where the sheet stops by the
        // time the sheet sets off for it.
        publish()

        hasBack = !!from
        open()
    })

    // Back, to the calendar or to wherever this was opened from, closes the
    // sheet; so does the way out of a deep link, which goes to the calendar
    // without history to go back through. Anything else is a page opening over
    // this one.
    base.on('exit', ({ to, isBack }: IRouteEvent) => isBack || to === '/' ? close() : leave())

    // A moon-sighting correction arriving mid-session moves the hijri occasions
    // listed on each slide, so redraw them where they stand. A saved occasion
    // has to appear on its day the same way, and so does a batch of the phone's
    // own events once it arrives.
    const refill = () => slider.fill()
    state.on('hijri', refill)
    state.on('custom-occasions', refill)
    state.on('phone-events', refill)
    state.on('phone-reminders', refill)
    state.on('events', refill)
    state.on('adoption', refill)
    // A country the user added lands asynchronously — the catalog is fetched —
    // so the slides redraw when it arrives, and again when one is added or
    // dropped.
    state.on('country-occasions', refill)

    // What this page last knew today to be. Kept so that when the day turns it
    // can tell the user resting on today from one who had swiped to another day
    // or opened one from the calendar.
    let known = calendar.today()

    // Midnight, with a day open. Resting on the day that was today means the
    // user is watching today, so the page moves on with it. Anyone else keeps
    // the day they were reading, and only the headings redraw — the day they
    // are on has just stopped being today, or has become it.
    const rolled = () => {
        const t = calendar.today()
        const resting = slider.getCursor() === 0
            && anchor.gy === known.gy && anchor.gm === known.gm && anchor.gd === known.gd
        known = t
        if (resting) {
            anchor = { gy: t.gy, gm: t.gm, gd: t.gd }
            // The address bar keeps yesterday's date until the next navigation
            // — the router can push a new entry but cannot replace this one, and
            // a pushed entry would put yesterday behind the back gesture.
            anchored = `${t.gy}-${t.gm}-${t.gd}`
            publish()
        }
        slider.fill()
    }
    state.on('today', rolled)

    base.on('unmounted', () => {
        state.off('day-pick', pick)
        state.off('day-sheet-top', stop)
        state.off('hijri', refill)
        state.off('custom-occasions', refill)
        state.off('phone-events', refill)
        state.off('phone-reminders', refill)
        state.off('events', refill)
        state.off('adoption', refill)
        state.off('country-occasions', refill)
        state.off('today', rolled)
    })

    return Object.assign(base, { warm })
}
